import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { MaterialIcon } from "@/components/MaterialIcon";
import { PageTitle, btnPrimary, btnSmall, card, inputCls } from "@/components/admin/ui";

const Row = ({ c, index, total, onMove, onRename, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(c.name);
  const commit = async () => {
    setEditing(false);
    if (name.trim() && name !== c.name) await onRename(c.id, name.trim());
    else setName(c.name);
  };
  return (
    <div className="flex flex-wrap items-center gap-3 p-3" data-testid={`category-row-${c.id}`}>
      <div className="min-w-[160px] flex-1">
        {editing ? (
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && commit()} className={`${inputCls} h-9`} data-testid="category-rename-input" />
        ) : (
          <button type="button" onClick={() => setEditing(true)} className="text-left text-[14px] font-semibold hover:underline truncate block max-w-full" data-testid="category-name">
            {c.name}
          </button>
        )}
        <p className="text-[12px] text-[#6B7280]">{c.video_count} video{c.video_count === 1 ? "" : "s"}</p>
      </div>
      <button type="button" className={btnSmall} disabled={index === 0} onClick={() => onMove(index, -1)} data-testid="category-move-up">Move up</button>
      <button type="button" className={btnSmall} disabled={index === total - 1} onClick={() => onMove(index, 1)} data-testid="category-move-down">Move down</button>
      <button type="button" className={`${btnSmall} text-[#B91C1C] border-[#FECACA] hover:bg-[#FEF2F2]`} onClick={() => onDelete(c)} data-testid="category-delete">Delete</button>
    </div>
  );
};

export default function AdminCategories() {
  const { data: cats, reload, setData } = useApi("/admin/categories");
  const [name, setName] = useState("");

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post("/admin/categories", { name });
      setName("");
      reload();
      toast.success("Category added");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const rename = async (id, newName) => {
    try {
      await api.put(`/admin/categories/${id}`, { name: newName });
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const move = async (index, dir) => {
    const ids = cats.map((c) => c.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(index + dir, 0, moved);
    try {
      const { data } = await api.put("/admin/categories/reorder", { ids });
      setData(data);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete category “${c.name}”? Videos will stay but leave this row.`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div data-testid="admin-categories-page">
      <PageTitle title="Categories" />
      <p className="text-[13px] text-[#6B7280] -mt-3 mb-5">Each category is a row on the home page, in this order. Assign videos to categories from the video details page.</p>
      <form onSubmit={add} className="flex gap-2 mb-5">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New category name, e.g. Spooky Performances" className={inputCls} data-testid="category-name-input" />
        <button type="submit" className={`${btnPrimary} shrink-0`} disabled={!name.trim()} data-testid="category-add-button">
          <MaterialIcon name="add" size={20} weight={600} />
          Add
        </button>
      </form>
      <div className={`${card} divide-y divide-[#E5E7EB]`} data-testid="category-list">
        {cats?.map((c, i) => <Row key={c.id} c={c} index={i} total={cats.length} onMove={move} onRename={rename} onDelete={del} />)}
        {cats && cats.length === 0 && <p className="p-8 text-center text-[14px] text-[#6B7280]">No categories yet.</p>}
      </div>
    </div>
  );
}
